import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  Suspense,
  useTransition,
} from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { MdMessage } from "react-icons/md";
import * as speechsdk from "microsoft-cognitiveservices-speech-sdk";

import VoiceBotHeader from "../components/Layout/VoiceBotHeader";
import VoiceBotFooter from "../components/Layout/VoiceBotFooter";
import InitialScreen from "../components/Layout/InitialScreen";
import ChatContainer from "../components/Layout/ChatContainer";
import ChatHistory from "../components/Chat/ChatHistory";
import {
  checkSessionExpiration,
  clearLoginTimestamp,
} from "../utils/sessionUtils";
import { useLanguage } from "../contexts/LanguageContext";
// import { customBranding, apiEndpoints } from "../config/config";
import {
  customBranding,
  apiEndpoints,
  authToken,
  accessToken,
} from "../config/config";

import { useChatbotConfig } from "../contexts/ChatbotConfigContext";
import {
  createNewChat,
  getChatMessages,
  saveChatMessages,
  updateChatHistory,
} from "../utils/chatStorageUtils";
import NewChat from "../components/Chat/NewChat";
import azureReminderService from "../services/azureReminderService";

const Talk = React.lazy(() => import("../components/Talk/Talk"));
const Chat = React.lazy(() => import("../components/Chat/Chat"));

const VoiceBot = () => {
  const navigate = useNavigate();
  const [isInChat, setIsInChat] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [recordingState, setRecordingState] = useState("Initial");
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [showTalk, setShowTalk] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showStatusForm, setShowStatusForm] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(null);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [currentSpeech, setCurrentSpeech] = useState(null);
  const [playingMessageId, setPlayingMessageId] = useState(null);
  const [speechDetected, setSpeechDetected] = useState(false);
  const { config } = useChatbotConfig();
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const chatBoxRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [abortController, setAbortController] = useState(null);
  const [activeSection, setActiveSection] = useState("Initial");
  const [isPending, startTransition] = useTransition();
  const [selectedHindiVoice, setSelectedHindiVoice] =
    useState("hi-IN-AaravNeural");
  // New states for chat history
  const [showChatHistory, setShowChatHistory] = useState(false);
  const [currentChat, setCurrentChat] = useState(null);
  const containerRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const lastScrollHeight = useRef(0);
  const lastScrollTop = useRef(0);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState("hi-IN-AaravNeural");

  const [supportedLanguages, setSupportedLanguages] = useState(["en"]);

  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return localStorage.getItem("isLoggedIn") === "true";
  });

  const [currentChatId, setCurrentChatId] = useState(null);
  const [isInNewChat, setIsInNewChat] = useState(false);
  const [isInExistingChat, setIsInExistingChat] = useState(false);
  const [existingChatMessages, setExistingChatMessages] = useState([]);
  const [loadingExistingChat, setLoadingExistingChat] = useState(false);
  const [chatBotData, setChatBotData] = useState(null);
  const [isScaling, setIsScaling] = useState(false);
  const [targetSection, setTargetSection] = useState(null);

  useEffect(() => {
    // When activeSection changes away from "talk", stop all audio
    if (activeSection !== "talk" && activeSection !== "Initial") {
      stopAllAudio();
    }
  }, [activeSection, stopAllAudio]);

  // In VoiceBot component
  const stopAllAudio = useCallback(() => {
    // Stop Azure audio if playing
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setCurrentAudio(null);
    }

    // Stop browser TTS if playing
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
    }

    // Reset playing state
    setPlayingMessageId(null);
    setCurrentSpeech(null);

    // Stop recording if active
    setRecordingState("Initial");
  }, [currentAudio]);

  const fetchExistingChatMessages = async (conversationId) => {
    try {
      setLoadingExistingChat(true);
      const response = await axios.get(
        apiEndpoints.conversation_messages(conversationId),
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            Accept: "application/json",
            clientToken: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Transform the API response to match our message format
      const formattedMessages = response.data.map((message) => ({
        id: message.id,
        type: message.sender_type === "user" ? "user" : "bot",
        content: message.message,
        timestamp: new Date(message.created_at),
      }));

      setExistingChatMessages(
        formattedMessages.length > 0
          ? formattedMessages
          : [
              {
                type: "bot",
                content: config.greetingMessage || "How can I help you?",
                timestamp: new Date(),
              },
            ]
      );
      setLoadingExistingChat(false);
    } catch (error) {
      console.error("Error fetching existing chat messages:", error);
      setExistingChatMessages([
        {
          type: "bot",
          content: "Failed to load conversation. Please try again.",
          timestamp: new Date(),
        },
      ]);
      setLoadingExistingChat(false);
    }
  };

  useEffect(() => {
    // Function to get and set voices
    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      setAvailableVoices(voices);

      // Find Hindi voice
      const hindiVoice = voices.find(
        (voice) =>
          voice.lang.includes("hi-IN") ||
          voice.name.toLowerCase().includes("hindi")
      );

      if (hindiVoice) {
        setSelectedVoice(hindiVoice);
        console.log("Hindi voice selected:", hindiVoice.name);
      } else {
        console.log("No Hindi voice found, available voices:", voices);
      }
    };

    // Load voices initially
    loadVoices();

    // Chrome loads voices asynchronously, so we need this event
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    return () => {
      if (window.speechSynthesis.onvoiceschanged !== undefined) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, []);

  // Add session check effect
  useEffect(() => {
    const checkSession = () => {
      if (isLoggedIn && checkSessionExpiration()) {
        // Session expired
        handleLogout();
        toast.info("Your session has expired. Please log in again.");
      }
    };

    // Check session immediately when component mounts
    checkSession();

    // Set up interval to check session every 5 minutes
    const sessionCheckInterval = setInterval(checkSession, 5 * 60 * 1000);

    // Clean up interval on component unmount
    return () => clearInterval(sessionCheckInterval);
  }, [isLoggedIn]);

  // Update handleLogout to clear login timestamp
  const handleLogout = useCallback(async () => {
    try {
      const authToken = localStorage.getItem("authToken");

      try {
        await axios.post(apiEndpoints.logout, null, {
          headers: {
            clientToken: `Bearer ${accessToken}`,
            Authorization: `Bearer ${authToken}`,
            Accept: "application/json",
          },
        });
      } catch (logoutError) {
        console.error("Logout API error:", logoutError);
      }

      // Clear login timestamp
      clearLoginTimestamp();

      toast.error("Logged out successfully");
      // Existing logout logic
      setIsLoggedIn(false);
      setShowStatusForm(false);
      localStorage.removeItem("isLoggedIn");
      localStorage.removeItem("authToken");
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          type: "bot",
          content: "Logged out successfully.",
          timestamp: new Date(),
        },
      ]);
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Error logging out. Please try again.");
    }
  }, [config.accessToken]);

  // Check for existing login state on mount
  useEffect(() => {
    const loginState = localStorage.getItem("isLoggedIn") === "true";
    if (loginState !== isLoggedIn) {
      setIsLoggedIn(loginState);
    }
  }, []);

  const toggleChat = useCallback(() => {
    startTransition(() => {
      setIsChatOpen((prevState) => {
        const newState = !prevState;
        window.parent.postMessage(newState ? "chatOpened" : "chatClosed", "*");
        
        if (!prevState && chatBotData?.module) {
          if (chatBotData.module.toLowerCase() === "talk") {
            setShowTalk(true);
            setShowChat(false);
            setShowChatHistory(false);
            setIsInChat(true);
            setActiveSection("talk");
            setMessages([]);
            setTimeout(() => {
              loadTalkChatHistory();
            }, 100);
          } else if (chatBotData.module.toLowerCase() === "chat") {
            setShowTalk(false);
            setShowChat(false);
            setShowChatHistory(true);
            setIsInChat(true);
            setActiveSection("chat-history");
          } else {
            setShowTalk(false);
            setShowChat(false);
            setShowChatHistory(false);
            setIsInChat(false);
            setActiveSection("Initial");
          }
        }
        return newState;
      });
    });
  }, [chatBotData, startTransition, loadTalkChatHistory]);

  useEffect(() => {
    // Send initial state to parent
    window.parent.postMessage("chatClosed", "*");
  }, []);

  const handleExpand = useCallback(() => {
    console.log("Expand button clicked!");
    try {
      const currentUrl = window.location.href;
      let parentUrl = document.referrer; // Fallback for parent URL
      // Define accessToken for localhost
      if (
        currentUrl.includes("localhost") ||
        currentUrl.includes("https://chatbot.bolofy.com/")
      ) {
        if (!config.accessToken) {
          config.accessToken =
            "eyJpdiI6ImZneWtXWm8wR1N1ZFJ6WktpaVJ3b2c9PSIsInZhbHVlIjoic1EzTThpcG0vT3JON0JQWllPVUxKYUk1R0dOeVV4WFVtKzFBMkxsYnprSDhwdnBYWGtlQnFGTS9EbFE3M1o1VEY4SWVENy81MVUxaWM1WFQ5RVZnM3J3bGo2SXQ0cGRnUmNHRTduRGZjT0RLemxkNWtyb1RZY0tnYWxRVTVuZkkiLCJtYWMiOiIwNzUyYmQxMzhkYzkzMmYyM2U2ZTAyNzZhZmRiNGI0OTdiYjk2MDliMjY0ZDRmM2NhYzkyOGUxMjYzZTFhOTIyIiwidGFnIjoiIn0=";
          console.log("Access token set for localhost:", config.accessToken);
        }

        // Function to update the current window's URL with reloading
        const updateCurrentWindowUrl = (fullParentUrl) => {
          console.log("Updating current window URL...");
          const baseUrl = currentUrl.split("?")[0]; // Remove existing query params

          // Add query parameters
          const params = new URLSearchParams();
          if (config.accessToken) params.set("accessToken", config.accessToken);
          params.set("url", encodeURIComponent(fullParentUrl));

          const updatedUrl = `${baseUrl}?${params.toString()}`;
          console.log("Updated current window URL:", updatedUrl);

          window.history.replaceState({}, "", updatedUrl);
        };
        updateCurrentWindowUrl(parentUrl);
      }
      // Function to process the expand action
      const proceedWithExpand = (fullParentUrl) => {
        console.log("Full parent URL:", fullParentUrl);

        // Parse the full parent URL
        const parentUrlObj = new URL(fullParentUrl);
        const fullPath = `${parentUrlObj.origin}${parentUrlObj.pathname}${parentUrlObj.search}`;

        // Add config and parent URL to parameters
        const configParams = encodeURIComponent(JSON.stringify(config));
        const separator = currentUrl.includes("?") ? "&" : "?";

        // Construct the expanded URL
        const expandedUrl = `${currentUrl}${separator}expandedConfig=${configParams}&url=${encodeURIComponent(
          fullPath
        )}`;

        // Verify URL construction
        const urlCheck = {
          original: fullPath,
          fullPathname: parentUrlObj.pathname,
          encoded: encodeURIComponent(fullPath),
          decoded: fullPath,
        };
        console.log("URL parameter check:", urlCheck);

        // Open new window
        const newWindow = window.open(expandedUrl, "_blank");
        if (newWindow) newWindow.focus();
      };

      // Check if running in an iframe
      if (window.parent !== window) {
        console.log(
          "Running inside an iframe, using postMessage to fetch parent URL."
        );

        // Listen for the parent URL from first logic
        window.addEventListener(
          "message",
          function (event) {
            if (event.data.type === "PARENT_URL_PATH") {
              parentUrl = event.data.fullUrl; // Get full URL with path from parent
              proceedWithExpand(parentUrl);
            }
          },
          { once: true }
        );

        // Send message to parent to fetch the URL
        window.parent.postMessage({ type: "GET_PARENT_URL" }, "*");
      } else {
        console.log("Not running in an iframe, using fallback for parent URL.");

        // If not in iframe, fallback to referrer or direct update
        proceedWithExpand(parentUrl || currentUrl); // Use fallback or current URL
      }
    } catch (error) {
      console.error("Error in handleExpand:", error);
    }
  }, [config]);

  // Add this event listener to the parent page
  useEffect(() => {
    const handleGetParentUrl = function (event) {
      if (event.data.type === "GET_PARENT_URL") {
        event.source.postMessage(
          {
            type: "PARENT_URL_PATH",
            fullUrl: window.location.href, // Send the parent URL
          },
          "*"
        );
      }
    };

    window.addEventListener("message", handleGetParentUrl);
    return () => window.removeEventListener("message", handleGetParentUrl);
  }, []);

  // Check if the bot is running in a new window
  const isExpandedWindow = window.opener != null;

  useEffect(() => {
    if (isExpandedWindow) {
      // Get the configuration from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const expandedConfig = urlParams.get("expandedConfig");

      if (expandedConfig) {
        try {
          const parsedConfig = JSON.parse(decodeURIComponent(expandedConfig));
          // Update the config through context
          // eslint-disable-next-line react-hooks/rules-of-hooks
          const { setConfig } = useChatbotConfig();
          setConfig(parsedConfig);
        } catch (error) {
          console.error("Error parsing expanded config:", error);
        }
      }
    }
  }, [isExpandedWindow]);

  // const handleToggleSpeech = useCallback(
  //   (text, messageId) => {
  //     if (playingMessageId === messageId) {
  //       if (window.speechSynthesis.speaking) {
  //         window.speechSynthesis.pause();
  //         setPlayingMessageId(null);
  //       } else {
  //         window.speechSynthesis.resume();
  //         setPlayingMessageId(messageId);
  //       }
  //     } else {
  //       if (window.speechSynthesis.speaking) {
  //         window.speechSynthesis.cancel();
  //       }
  //       const utterance = new SpeechSynthesisUtterance(text);
  //       utterance.onstart = () => {
  //         setPlayingMessageId(messageId);
  //       };
  //       utterance.onend = () => {
  //         setPlayingMessageId(null);
  //       };
  //       utterance.onpause = () => setPlayingMessageId(null);
  //       utterance.onresume = () => setPlayingMessageId(messageId);
  //       window.speechSynthesis.speak(utterance);
  //       setCurrentSpeech({ utterance, messageId });
  //     }
  //   },
  //   [playingMessageId]
  // );

  // Wrap the function with useCallback
  const synthesizeSpeechWithSdk = useCallback(
    async (text, language = "hi-IN") => {
      const speechKey = process.env.REACT_APP_AZURE_SPEECH_KEY;
      const speechRegion = process.env.REACT_APP_AZURE_REGION;
      const voiceName = selectedHindiVoice;

      // Use REST API instead of SDK to get audio without playing
      try {
        const response = await fetch(
          `https://${speechRegion}.tts.speech.microsoft.com/cognitiveservices/v1`,
          {
            method: "POST",
            headers: {
              "Ocp-Apim-Subscription-Key": speechKey,
              "Content-Type": "application/ssml+xml",
              "X-Microsoft-OutputFormat": "audio-16khz-128kbitrate-mono-mp3",
            },
            body: `<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xml:lang="${language}">
            <voice name="${voiceName}">
              ${text}
            </voice>
          </speak>`,
          }
        );

        if (!response.ok) {
          throw new Error(`Azure Speech API error: ${response.status}`);
        }

        const audioData = await response.arrayBuffer();
        const audioBlob = new Blob([audioData], { type: "audio/mp3" });
        const audioUrl = URL.createObjectURL(audioBlob);

        return {
          success: true,
          audioUrl: audioUrl,
          audioBlob: audioBlob,
        };
      } catch (error) {
        throw error;
      }
    },
    [selectedHindiVoice]
  );

  // Updated handleToggleSpeech function with proper state management
  const handleToggleSpeech = useCallback(
    async (text, messageId) => {
      // Handle clicking on currently playing message
      if (playingMessageId === messageId) {
        if (currentAudio) {
          if (!currentAudio.paused) {
            currentAudio.pause();
            setPlayingMessageId(null);
          } else {
            currentAudio.play();
            setPlayingMessageId(messageId);
          }
        }
        return;
      }

      // Stop any currently playing audio
      if (currentAudio) {
        currentAudio.pause();
        setCurrentAudio(null);
      }
      if (window.speechSynthesis.speaking) {
        window.speechSynthesis.cancel();
      }

      setPlayingMessageId("loading");

      try {
        // Get audio URL from Azure without playing it
        const result = await synthesizeSpeechWithSdk(text);

        // Create Audio element and control playback manually
        const audio = new Audio(result.audioUrl);

        audio.onplay = () => setPlayingMessageId(messageId);
        audio.onpause = () => setPlayingMessageId(null);
        audio.onended = () => {
          setPlayingMessageId(null);
          URL.revokeObjectURL(result.audioUrl);
          setCurrentAudio(null);
        };

        // Store the audio element
        setCurrentAudio(audio);

        // Play the audio
        await audio.play();
      } catch (error) {
        console.error("Azure SDK error:", error);
        toast.warning("Using browser speech synthesis instead.");

        // Fallback to browser TTS
        const utterance = new SpeechSynthesisUtterance(text);

        if (selectedVoice) {
          utterance.voice = selectedVoice;
          utterance.lang = selectedVoice.lang;
        }

        utterance.onstart = () => setPlayingMessageId(messageId);
        utterance.onend = () => setPlayingMessageId(null);
        utterance.onpause = () => setPlayingMessageId(null);
        utterance.onresume = () => setPlayingMessageId(messageId);

        window.speechSynthesis.speak(utterance);
        setCurrentSpeech({ utterance, messageId });
      }
    },
    [playingMessageId, selectedVoice, currentAudio, synthesizeSpeechWithSdk]
  );

  // Note: The above code is a simplified version of the original function. It uses the Azure SDK for speech synthesis and falls back to the browser's speech synthesis if the SDK fails. The selected voice is passed as a parameter to the SDK function.
  // The function also handles the state of the speech synthesis, including play, pause, and resume functionality.

  //given solution is not using sdk service***********IMPORTANT************************88888888888888888888888888888

  // const handleToggleSpeech = useCallback(
  //   async (text, messageId) => {
  //     if (playingMessageId === messageId) {
  //       // If the same message is being played, toggle pause/resume
  //       if (currentAudio) {
  //         if (!currentAudio.paused) {
  //           currentAudio.pause();
  //           setPlayingMessageId(null);
  //         } else {
  //           await currentAudio.play();
  //           setPlayingMessageId(messageId);
  //         }
  //       }
  //       return;
  //     }

  //     // Stop any currently playing audio
  //     if (currentAudio) {
  //       currentAudio.pause();
  //       setCurrentAudio(null);
  //     }

  //     try {
  //       setPlayingMessageId("loading");

  //       // Get Azure credentials from environment or config
  //       // This approach keeps credentials more secure
  //       const apiKey = "8IqYR1kM2HKuWzIT8P2qppEg6KpRjshaR8QF98YDer9TEPhFin6YJQQJ99BEACYeBjFXJ3w3AAAYACOGfJdR";
  //       const region = "eastus";

  //       console.log("Using Azure key:", apiKey ? "Key provided" : "No key");

  //       // Check if we have proper credentials before making the API call
  //       if (!apiKey) {
  //         throw new Error("Azure Speech API key not configured");
  //       }

  //       // Create SSML
  //       const ssml = `
  //         <speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xml:lang="hi-IN">
  //           <voice name="hi-IN-SwaraNeural">
  //             ${text}
  //           </voice>
  //         </speak>
  //       `;

  //       // Make API request with proper headers
  //       const response = await fetch(
  //         `https://${region}.tts.speech.microsoft.com/cognitiveservices/v1`,
  //         {
  //           method: 'POST',
  //           headers: {
  //             'Ocp-Apim-Subscription-Key': apiKey,
  //             'Content-Type': 'application/ssml+xml',
  //             'X-Microsoft-OutputFormat': 'audio-16khz-128kbitrate-mono-mp3'
  //           },
  //           body: ssml
  //         }
  //       );

  //       if (!response.ok) {
  //         const errorText = await response.text().catch(() => "Unknown error");
  //         console.error(`Azure Speech API error: ${response.status}`, errorText);
  //         throw new Error(`Azure Speech API error: ${response.status}`);
  //       }

  //       // Process audio data
  //       const audioData = await response.arrayBuffer();
  //       const audioBlob = new Blob([audioData], { type: 'audio/mp3' });
  //       const audioUrl = URL.createObjectURL(audioBlob);

  //       // Create and play the audio
  //       const audio = new Audio(audioUrl);

  //       audio.onplay = () => setPlayingMessageId(messageId);
  //       audio.onpause = () => setPlayingMessageId(null);
  //       audio.onended = () => {
  //         setPlayingMessageId(null);
  //         URL.revokeObjectURL(audioUrl);
  //       };

  //       await audio.play();
  //       setCurrentAudio(audio);

  //     } catch (error) {
  //       console.error("Azure Speech API error:", error);
  //       toast.error("Text-to-speech service unavailable. Using browser synthesis instead.");

  //       // Fall back to browser's speech synthesis
  //       if (window.speechSynthesis) {
  //         const utterance = new SpeechSynthesisUtterance(text);

  //         if (selectedVoice) {
  //           utterance.voice = selectedVoice;
  //           utterance.lang = selectedVoice.lang;
  //         }

  //         utterance.onstart = () => setPlayingMessageId(messageId);
  //         utterance.onend = () => setPlayingMessageId(null);
  //         utterance.onpause = () => setPlayingMessageId(null);
  //         utterance.onresume = () => setPlayingMessageId(messageId);

  //         window.speechSynthesis.speak(utterance);
  //         setCurrentSpeech({ utterance, messageId });
  //       } else {
  //         setPlayingMessageId(null);
  //         toast.error("Text-to-speech is not available in this browser.");
  //       }
  //     }
  //   },
  //   [playingMessageId, currentAudio, selectedVoice, config, setPlayingMessageId, setCurrentAudio, setCurrentSpeech]
  // );

  // Load messages when current chat changes
  useEffect(() => {
    if (currentChat?.id) {
      const savedMessages = getChatMessages(currentChat.id);
      setMessages(
        savedMessages.length > 0
          ? savedMessages
          : [
              {
                type: "bot",
                content: config.greetingMessage || "",
                timestamp: new Date(),
              },
            ]
      );
    }
  }, [currentChat?.id, config.greetingMessage]);

  // Save messages when they change
  useEffect(() => {
    if (currentChat?.id && messages.length > 0) {
      saveChatMessages(currentChat.id, messages);
    }
  }, [currentChat?.id, messages]);

  const handleStartNewChat = useCallback(() => {
    startTransition(() => {
      const newChat = createNewChat();
      setCurrentChat(newChat);
      setShowChatHistory(false);
      setShowChat(true);
      setActiveSection("chat");
      setMessages([
        {
          type: "bot",
          content: config.greetingMessage || "How can I help you?",
          timestamp: new Date(),
        },
      ]);
    });
    sendScalingMessage(true);
  }, [config.greetingMessage]);

  const handleChatClick = useCallback(() => {
    startTransition(() => {
      setShowChatHistory(true);
      setShowChat(false);
      setShowTalk(false);
      setIsInChat(true);
      setActiveSection("chat-history");
      sendScalingMessage(true);
    });
  }, [
    startTransition,
    setShowChatHistory,
    setShowChat,
    setShowTalk,
    setIsInChat,
    setActiveSection,
  ]);

  const handleSelectChat = useCallback((chat) => {
    startTransition(() => {
      setCurrentChat(chat);
      setShowChatHistory(false);
      setShowChat(true);
      setActiveSection("chat");
    });
  }, []);

  const handleSelectExistingChat = useCallback((conversationId) => {
    console.log("Selected conversation ID:", conversationId);
    setCurrentChatId(conversationId);
    setIsInExistingChat(true);
    setShowChatHistory(false);
    setShowChat(false);
    setShowTalk(false);
    setActiveSection("existing-chat");
    fetchExistingChatMessages(conversationId);
  }, []);

  const handleSelectAiChat = useCallback(
    (aiChatData) => {
      startTransition(() => {
        setCurrentChat(aiChatData);

        // Load messages from the AI chat data
        if (
          aiChatData &&
          Array.isArray(aiChatData.chatMessages) &&
          aiChatData.chatMessages.length > 0
        ) {
          setMessages(aiChatData.chatMessages);
        } else {
          // Fallback to default greeting
          setMessages([
            {
              type: "bot",
              content: config.greetingMessage || "How can I help you?",
              timestamp: new Date(),
            },
          ]);
        }

        setShowChatHistory(false);
        setShowChat(true);
        setActiveSection("chat");
      });
    },
    [config.greetingMessage, startTransition]
  );
  // Fixed handleNotification function in VoiceBot.js
  // Replace your existing handleNotification function with this:

  const handleNotification = (notification) => {
    console.log("📨 Received notification:", notification);

    // Create message content based on notification
    const messageContent =
      notification.message ||
      `🔔 Reminder: Meeting with ${notification.clientName} is coming up!`;

    // Add to current messages state (for immediate display)
    setMessages((prev) => [
      ...prev,
      {
        type: "bot",
        content: messageContent,
        timestamp: new Date(notification.timestamp || new Date()),
        isReminderNotification: true,
        reminderData: notification,
        source: "azure",
      },
    ]);

    // Show browser notification if permission granted
    if (Notification.permission === "granted") {
      const browserNotification = new Notification("Meeting Reminder", {
        body: messageContent,
        icon: "/favicon.ico",
        tag: `reminder-${notification.notificationId}`,
        requireInteraction: true,
      });

      browserNotification.onclick = () => {
        window.focus();
        browserNotification.close();
        // Switch to reminder chat when notification is clicked
        handleSelectReminderChat();
      };

      // Auto-close after 10 seconds if user doesn't interact
      setTimeout(() => {
        browserNotification.close();
      }, 10000);
    }

    // Show toast notification
    toast.info(messageContent, {
      position: "top-right",
      autoClose: 10000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      icon: "🔔",
    });

    // FIXED: Properly update chatbot_reminders in localStorage
    try {
      const savedReminders = localStorage.getItem("chatbot_reminders");
      let reminders = [];

      if (savedReminders) {
        try {
          reminders = JSON.parse(savedReminders);
          // Ensure it's an array
          if (!Array.isArray(reminders)) {
            reminders = [];
          }
        } catch (error) {
          console.error("Error parsing existing reminders:", error);
          reminders = [];
        }
      }

      // Find the reminder by reminderId
      const existingReminderIndex = reminders.findIndex(
        (r) => r.reminderId === notification.reminderId
      );
      const meetingDate = new Date(notification.meetingDate);
      const now = new Date();
      const isPastMeeting = meetingDate < now;
      const notificationEntry = {
        type: notification.reminderType || "reminder",
        content: messageContent,
        timestamp: notification.timestamp || new Date().toISOString(),
      };
      if (existingReminderIndex !== -1) {
        // Update existing reminder: push notification to notificationMessages
        const reminder = reminders[existingReminderIndex];
        if (!Array.isArray(reminder.notificationMessages)) {
          reminder.notificationMessages = [];
        }
        reminder.notificationMessages.push(notificationEntry);
        reminder.status = isPastMeeting ? "completed" : "active";
        reminders[existingReminderIndex] = reminder;
        console.log(
          "✅ Updated existing reminder with notification message array"
        );
      } else {
        // Create new reminder entry (edge case)
        reminders.push({
          reminderId: notification.reminderId,
          clientName: notification.clientName,
          meetingDate: notification.meetingDate,
          createdAt: notification.timestamp || new Date().toISOString(),
          status: isPastMeeting ? "completed" : "active",
          schedulingMessage: "", // unknown, since not scheduled from this client
          notificationMessages: [notificationEntry],
          source: "azure",
        });
        console.log(
          "✅ Added new reminder notification to storage (array style)"
        );
      }

      // Save updated reminders back to localStorage
      localStorage.setItem("chatbot_reminders", JSON.stringify(reminders));
      console.log(
        "💾 Reminders saved to localStorage:",
        reminders.length,
        "total reminders"
      );

      // Dispatch custom event to notify other components that reminders have been updated
      window.dispatchEvent(
        new CustomEvent("reminders-updated", {
          detail: { reminders, newNotification: notification },
        })
      );
    } catch (error) {
      console.error("❌ Error updating reminders in localStorage:", error);
    }
  };

  useEffect(() => {
    const handleRemindersUpdated = (event) => {
      console.log("📢 Reminders updated event received:", event.detail);

      // If we're currently in the reminder chat, refresh the messages
      if (activeSection === "chat" && showChat) {
        // Check if current messages contain reminder messages
        const hasReminderMessages = messages.some(
          (msg) => msg.isReminderMessage || msg.isReminderNotification
        );

        if (hasReminderMessages) {
          console.log("🔄 Refreshing reminder chat with updated data");
          // Refresh the reminder chat
          handleSelectReminderChat();
        }
      }
    };

    window.addEventListener("reminders-updated", handleRemindersUpdated);

    return () => {
      window.removeEventListener("reminders-updated", handleRemindersUpdated);
    };
  }, [activeSection, showChat, messages, handleSelectReminderChat]);

  const handleSelectReminderChat = useCallback(() => {
    startTransition(() => {
      // Retrieve and parse reminder data from local storage
      const savedReminders = localStorage.getItem("chatbot_reminders");
      let reminderMessages = [];

      if (savedReminders) {
        try {
          const reminders = JSON.parse(savedReminders);
          if (Array.isArray(reminders)) {
            // Sort reminders by creation date (newest first)
            const sortedReminders = reminders.sort(
              (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
            );
            // For each reminder, group scheduling and notification messages
            sortedReminders.forEach((reminder) => {
              const meetingDate = new Date(reminder.meetingDate);
              const now = new Date();
              const isPastMeeting = meetingDate < now;
              // Always show the scheduling message first
              if (reminder.schedulingMessage) {
                reminderMessages.push({
                  type: "bot",
                  content: reminder.schedulingMessage,
                  timestamp: new Date(reminder.createdAt),
                  isReminderMessage: true,
                  reminderData: reminder,
                });
              }
              if (isPastMeeting) {
                // Show only the meeting completed message
                const formattedDate = meetingDate.toLocaleDateString("en-IN");
                const formattedTime = meetingDate.toLocaleTimeString("en-IN", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                });
                reminderMessages.push({
                  type: "bot",
                  content: `✅ Meeting completed with ${reminder.clientName} on ${formattedDate} at ${formattedTime}`,
                  timestamp: meetingDate,
                  isReminderMessage: true,
                  reminderData: reminder,
                });
              } else {
                // Show all notification messages (in order)
                if (Array.isArray(reminder.notificationMessages)) {
                  reminder.notificationMessages.forEach((nm) => {
                    reminderMessages.push({
                      type: "bot",
                      content: nm.content,
                      timestamp: new Date(nm.timestamp),
                      isReminderMessage: true,
                      reminderData: reminder,
                    });
                  });
                }
              }
            });
          }
        } catch (error) {
          console.error("Error parsing reminders:", error);
        }
      }

      // Add welcome message if no reminders exist
      if (reminderMessages.length === 0) {
        reminderMessages = [
          {
            type: "bot",
            content:
              "Welcome to your meeting reminders! 📅\n\nNo reminders found. Type '/reminder' or use the reminder button to schedule your first meeting reminder.\n\nI'll automatically send you notifications 30, 15, and 5 minutes before your meetings!",
            timestamp: new Date(),
            isWelcomeMessage: true,
          },
        ];
      } else {
        // Add summary message at the beginning
        const totalReminders = reminderMessages.filter(
          (msg) => msg.isReminderMessage && msg.reminderData
        ).length;
        const activeReminders = reminderMessages.filter((msg) => {
          if (msg.reminderData) {
            const meetingDate = new Date(msg.reminderData.meetingDate);
            return meetingDate > new Date();
          }
          return false;
        }).length;
        const summaryMessage = {
          type: "bot",
          content: `📊 **Meeting Reminders Summary**\n\nTotal reminders: ${totalReminders}\nActive (upcoming): ${activeReminders}\nCompleted: ${
            totalReminders - activeReminders
          }\n\nType '/reminder' to schedule a new meeting reminder.`,
          timestamp: new Date(),
          isSummaryMessage: true,
        };
        reminderMessages.unshift(summaryMessage);
      }

      // Set messages and navigate to chat
      setMessages(reminderMessages);
      setShowChatHistory(false);
      setShowChat(true);
      setActiveSection("chat");
    });
  }, [
    startTransition,
    setMessages,
    setShowChatHistory,
    setShowChat,
    setActiveSection,
  ]);

  const handleBackToChatHistory = useCallback(() => {
    console.log("Navigating back from NewChat to chat history");
    setIsInExistingChat(false);
    setShowChatHistory(true);
    setActiveSection("chat-history");
    setCurrentChatId(null);
  }, []);

  useEffect(() => {
    const handleMessageFromNestedComponents = (event) => {
      if (event.data && event.data.type === "NEWCHAT_BACK_BUTTON_CLICKED") {
        console.log("Received back button message from NewChat");
        setIsInExistingChat(false);
        setShowChatHistory(true);
        setActiveSection("chat-history");
        setCurrentChatId(null);
      }
    };

    window.addEventListener("message", handleMessageFromNestedComponents);
    return () => {
      window.removeEventListener("message", handleMessageFromNestedComponents);
    };
  }, []);

  // UPDATED loadTalkChatHistory function with better logic:
  const loadTalkChatHistory = useCallback(() => {
    console.log("🔍 Checking talk_chats data...");

    const { hasData, messages: talkMessages } = checkTalkChatsData();

    if (hasData) {
      // Data exists - load existing chat
      console.log(
        "📋 Found existing data, loading",
        talkMessages.length,
        "messages"
      );
      setMessages(talkMessages);

      // Scroll to bottom after loading existing messages
      setTimeout(() => {
        if (chatBoxRef.current) {
          chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
          console.log("📍 Scrolled to bottom with existing messages");
        }
      }, 200); // Increased timeout for better reliability
    } else {
      // No data - create initial greeting
      console.log("🆕 No data found, creating initial greeting");
      const initialMessages = createInitialGreeting();
      setMessages(initialMessages);

      // Scroll to bottom after creating new greeting
      setTimeout(() => {
        if (chatBoxRef.current) {
          chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
          console.log("📍 Scrolled to bottom with new greeting");
        }
      }, 200);
    }
  }, [checkTalkChatsData, createInitialGreeting, setMessages]);

  const sendScalingMessage = useCallback((shouldScale) => {
    const message = shouldScale ? 'chatScaled' : 'chatUnscaled';
    window.parent.postMessage(message, '*');
  }, []);

  // UPDATED handleTalkClick with scale effect
  const handleTalkClick = useCallback(() => {
    console.log("🎤 Entering talk mode...");

    
    
    startTransition(() => {
      setShowTalk(true);
      setShowChat(false);
      setShowChatHistory(false);
      setIsInChat(true);
      setActiveSection("talk");
      setMessages([]);
      sendScalingMessage(true);
      
      setTimeout(() => {
        loadTalkChatHistory();
      }, 100);
    });
  }, [
    startTransition,
    setShowTalk,
    setShowChat,
    setShowChatHistory,
    setIsInChat,
    setActiveSection,
    setMessages,
    loadTalkChatHistory,
  ]);

  // UPDATED effect for scrolling in talk mode:
  useEffect(() => {
    if (activeSection === "talk" && chatBoxRef.current && messages.length > 0) {
      setTimeout(() => {
        chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
        console.log("📍 Auto-scrolled to bottom in talk mode");
      }, 100);
    }
  }, [messages, activeSection]);

  const cancelOngoingRequests = useCallback(() => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }
  }, [abortController]);

  const handleBackClick = useCallback(() => {
    // Stop audio when navigating away from Talk
    if (activeSection === "talk") {
      stopAllAudio();
    }

    startTransition(() => {
      if (isInExistingChat) {
        setIsInExistingChat(false);
        setShowChatHistory(true);
        setActiveSection("chat-history");
      } else if (showChat) {
        // Save current chat state before going back
        if (currentChat?.id) {
          saveChatMessages(currentChat.id, messages);
        }
        setShowChatHistory(true);
        setShowChat(false);
        setShowTalk(false);
        setActiveSection("chat-history");
      } else if (showTalk) {
        setShowTalk(false);
        setIsInChat(false);
        setActiveSection("Initial");
        sendScalingMessage(false);
      } else if (showChatHistory) {
        setShowChatHistory(false);
        setIsInChat(false);
        setActiveSection("Initial");
      } else if (showParticipants) {
        setShowParticipants(false);
        setShowChatHistory(true);
        setActiveSection("chat-history");
        sendScalingMessage(false);
      }
      cancelOngoingRequests();
    });
  }, [
    cancelOngoingRequests,
    showChat,
    showTalk,
    stopAllAudio,
    showChatHistory,
    currentChat,
    messages,
    isInExistingChat,
    showParticipants,
  ]);


  // Update Messages wrapper function to ensure proper storage
  const updateMessages = useEffect(() => {
    const container = chatBoxRef.current;
    if (!container || messages.length === 0) return;

    const lastMessage = messages[messages.length - 1];

    if (lastMessage.type === "user") {
      // For user messages - scroll to bottom immediately
      setTimeout(() => {
        container.scrollTop = container.scrollHeight;
      }, 50);
    } else if (lastMessage.type === "bot") {
      // For bot messages - scroll to show just a peek
      setTimeout(() => {
        const scrollAmount =
          container.scrollHeight - container.clientHeight - 150; // Adjust 100 to show more/less
        container.scrollTop = scrollAmount;
      }, 50);
    }
  }, [messages]);

  useEffect(() => {
    const needsScaling = [
      "talk", 
      "chat", 
      "chat-history", 
      "existing-chat"
    ].includes(activeSection);
    
    sendScalingMessage(needsScaling);
  }, [activeSection, sendScalingMessage]);

  useEffect(() => {
    const FetchChatBot = async () => {
      try {
        const response = await axios.get(apiEndpoints.chatbot_config, {
          headers: {
            clientToken: `Bearer ${accessToken}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });
        setChatBotData(response.data);
        localStorage.setItem("chatBotData", JSON.stringify(response.data));
        let langs = response.data.languages;
        // Fallback array
        const fallbackLangs = [
          { code: "en", name: "English" },
          { code: "hi", name: "Hindi" },
          { code: "gu", name: "Gujarati" },
          { code: "kn", name: "Kannada" },
        ];
        // If no data or empty array, use fallback
        if (!Array.isArray(langs) || langs.length === 0) {
          langs = fallbackLangs;
        } else if (typeof langs[0] === "object" && langs[0] !== null && "code" in langs[0] && "name" in langs[0]) {
          // If already in correct format, ensure English is present
          if (!langs.some(l => l.code === "en")) {
            langs = [{ code: "en", name: "English" }, ...langs];
          }
        } else {
          langs = fallbackLangs;
        }
        setSupportedLanguages(langs);
      } catch (error) {
        setSupportedLanguages([
          { code: "en", name: "English" },
          { code: "hi", name: "Hindi" },
          { code: "gu", name: "Gujarati" },
          { code: "kn", name: "Kannada" },
        ]);
        console.error("Error fetching chatbot config:", error);
      }
    };
    FetchChatBot();
  }, [accessToken]);

  useEffect(() => {
    const handleAudioEnded = (event) => {
      if (event.target.src === audioPlaying) {
        setAudioPlaying(null);
      }
    };

    document.addEventListener("ended", handleAudioEnded, true);

    return () => {
      document.removeEventListener("ended", handleAudioEnded, true);
    };
  }, [audioPlaying]);

  const toggleAudioPlayback = useCallback(
    (audioUrl) => {
      if (audioPlaying === audioUrl) {
        if (currentAudio) {
          currentAudio.pause();
          setAudioPlaying(null);
        }
      } else {
        if (currentAudio && audioPlaying) {
          currentAudio.pause();
        }

        const audio = document.getElementById(audioUrl) || new Audio(audioUrl);
        audio.onplay = () => setAudioPlaying(audioUrl);
        audio.onpause = () => setAudioPlaying(null);
        audio.onended = () => setAudioPlaying(null);
        audio.play().catch((e) => console.error("Play error:", e));
        setCurrentAudio(audio);
      }
    },
    [audioPlaying, currentAudio]
  );

  // const playAudioAutomatically = useCallback((audioUrl) => {
  //   const audio = new Audio(audioUrl);
  //   audio.onplay = () => setAudioPlaying(audioUrl);
  //   audio.onpause = () => setAudioPlaying(null);
  //   audio.onended = () => setAudioPlaying(null);
  //   audio.play().catch((e) => console.error("Automatic play error:", e));
  //   setCurrentAudio(audio);
  // }, []);

  useEffect(() => {
    const handleReminderChatMessage = (event) => {
      const { clientName, meetingDate, message, createdAt } = event.detail;

      // Add the message to the chat
      setMessages((prev) => [
        ...prev,
        {
          type: "bot",
          content: message,
          timestamp: new Date(createdAt),
        },
      ]);

      // Also add to reminders in localStorage for persistence
      const savedReminders = localStorage.getItem("chatbot_reminders");
      let reminders = [];
      if (savedReminders) {
        try {
          reminders = JSON.parse(savedReminders);
        } catch {}
      }
      // Optionally, you can add a flag or a new message to the reminder object
      reminders.push({
        clientName,
        meetingDate,
        reminderDate: new Date().toISOString(),
        createdAt,
        notificationSent: true,
        chatMessage: message,
      });
      localStorage.setItem("chatbot_reminders", JSON.stringify(reminders));
    };

    window.addEventListener("reminder-chat-message", handleReminderChatMessage);
    return () =>
      window.removeEventListener(
        "reminder-chat-message",
        handleReminderChatMessage
      );
  }, [setMessages]);

  useEffect(() => {
    console.log("🚀 Setting up Azure notification polling...");

    // Test connection first
    azureReminderService.testConnection().then((result) => {
      if (result.success) {
        console.log("✅ Azure Functions connection test passed");
      } else {
        console.error(
          "❌ Azure Functions connection test failed:",
          result.error
        );
      }
    });

    const handleNotification = (notification) => {
      console.log("📨 Received notification:", notification);

      // Create message content based on notification
      const messageContent =
        notification.message ||
        `🔔 Reminder: Meeting with ${notification.clientName} is coming up!`;

      // Add to current messages state (for immediate display)
      setMessages((prev) => [
        ...prev,
        {
          type: "bot",
          content: messageContent,
          timestamp: new Date(notification.timestamp || new Date()),
          isReminderNotification: true,
          reminderData: notification,
          source: "azure",
        },
      ]);

      // Show browser notification if permission granted
      if (Notification.permission === "granted") {
        const browserNotification = new Notification("Meeting Reminder", {
          body: messageContent,
          icon: "/favicon.ico",
          tag: `reminder-${notification.notificationId}`,
          requireInteraction: true,
        });

        browserNotification.onclick = () => {
          window.focus();
          browserNotification.close();
          // Switch to reminder chat when notification is clicked
          handleSelectReminderChat();
        };

        // Auto-close after 10 seconds if user doesn't interact
        setTimeout(() => {
          browserNotification.close();
        }, 10000);
      }

      // Show toast notification
      toast.info(messageContent, {
        position: "top-right",
        autoClose: 10000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        icon: "🔔",
      });

      // FIXED: Properly update chatbot_reminders in localStorage
      try {
        const savedReminders = localStorage.getItem("chatbot_reminders");
        let reminders = [];

        if (savedReminders) {
          try {
            reminders = JSON.parse(savedReminders);
            // Ensure it's an array
            if (!Array.isArray(reminders)) {
              reminders = [];
            }
          } catch (error) {
            console.error("Error parsing existing reminders:", error);
            reminders = [];
          }
        }

        // Find the reminder by reminderId
        const existingReminderIndex = reminders.findIndex(
          (r) => r.reminderId === notification.reminderId
        );
        const meetingDate = new Date(notification.meetingDate);
        const now = new Date();
        const isPastMeeting = meetingDate < now;
        const notificationEntry = {
          type: notification.reminderType || "reminder",
          content: messageContent,
          timestamp: notification.timestamp || new Date().toISOString(),
        };
        if (existingReminderIndex !== -1) {
          // Update existing reminder: push notification to notificationMessages
          const reminder = reminders[existingReminderIndex];
          if (!Array.isArray(reminder.notificationMessages)) {
            reminder.notificationMessages = [];
          }
          reminder.notificationMessages.push(notificationEntry);
          reminder.status = isPastMeeting ? "completed" : "active";
          reminders[existingReminderIndex] = reminder;
          console.log(
            "✅ Updated existing reminder with notification message array"
          );
        } else {
          // Create new reminder entry (edge case)
          reminders.push({
            reminderId: notification.reminderId,
            clientName: notification.clientName,
            meetingDate: notification.meetingDate,
            createdAt: notification.timestamp || new Date().toISOString(),
            status: isPastMeeting ? "completed" : "active",
            schedulingMessage: "", // unknown, since not scheduled from this client
            notificationMessages: [notificationEntry],
            source: "azure",
          });
          console.log(
            "✅ Added new reminder notification to storage (array style)"
          );
        }

        // Save updated reminders back to localStorage
        localStorage.setItem("chatbot_reminders", JSON.stringify(reminders));
        console.log(
          "💾 Reminders saved to localStorage:",
          reminders.length,
          "total reminders"
        );

        // Dispatch custom event to notify other components that reminders have been updated
        window.dispatchEvent(
          new CustomEvent("reminders-updated", {
            detail: { reminders, newNotification: notification },
          })
        );
      } catch (error) {
        console.error("❌ Error updating reminders in localStorage:", error);
      }
    };

    // Start polling every 15 seconds
    azureReminderService.startNotificationPolling(handleNotification, 15000);

    // Request notification permission if not already granted
    if (Notification.permission === "default") {
      Notification.requestPermission().then((permission) => {
        if (permission === "granted") {
          console.log("✅ Notification permission granted");
        } else {
          console.log("❌ Notification permission denied");
        }
      });
    }

    // Cleanup on unmount
    return () => {
      azureReminderService.stopNotificationPolling();
    };
  }, []); // Add dependencies as needed

  // useEffect(() => {
  //   // Only initialize talk_chats with greeting if not present
  //   if (!localStorage.getItem("talk_chats")) {
  //     const greetingMsg = {
  //       type: "bot",
  //       content: config.greetingMessage || "How can I help you?",
  //       timestamp: new Date().toISOString(),
  //       language: selectedVoice?.lang || "en"
  //     };
  //     localStorage.setItem("talk_chats", JSON.stringify([greetingMsg]));
  //   }
  // }, [config.greetingMessage, selectedVoice]);

  // Helper to check if talk_chats has any data
  const checkTalkChatsData = useCallback(() => {
    try {
      const existingTalkChats = localStorage.getItem("talk_chats");

      if (!existingTalkChats) {
        return { hasData: false, messages: [] };
      }

      const parsedChats = JSON.parse(existingTalkChats);

      if (Array.isArray(parsedChats) && parsedChats.length > 0) {
        return { hasData: true, messages: parsedChats };
      } else {
        return { hasData: false, messages: [] };
      }
    } catch (error) {
      console.error("Error parsing talk_chats:", error);
      // Clear corrupted data
      localStorage.removeItem("talk_chats");
      return { hasData: false, messages: [] };
    }
  }, []);

  // Helper to create initial greeting message
  const createInitialGreeting = useCallback(() => {
    const greetingMsg = {
      type: "bot",
      content: config.greetingMessage || "How can I help you?",
      timestamp: new Date().toISOString(),
      language: selectedVoice?.lang || "en",
    };

    localStorage.setItem("talk_chats", JSON.stringify([greetingMsg]));
    console.log("🆕 Created initial greeting message for talk_chats");
    return [greetingMsg];
  }, [config.greetingMessage, selectedVoice]);

  // Helper to save a talk message (NEVER save greeting messages)
  const saveTalkChatMessage = useCallback(
    (message, language) => {
      console.log(
        "🔍 Attempting to save message:",
        message?.type,
        message?.content?.substring(0, 50)
      );

      // Skip greeting messages completely
      const isGreetingMessage =
        message.content === (config.greetingMessage || "How can I help you?") &&
        message.type === "bot";
      if (isGreetingMessage) {
        console.log("⏭️ Skipping greeting message - never save these");
        return;
      }

      // Skip recording messages
      const isRecordingMessage =
        message.content === "Recording in progress..." ||
        message.content === "Stopped voice recording." ||
        message.content === "No speech detected. Recording stopped.";

      if (isRecordingMessage) {
        console.log("⏭️ Skipping recording message");
        return;
      }

      const talkChats = JSON.parse(localStorage.getItem("talk_chats") || "[]");

      // Check for duplicate messages
      const messageExists = talkChats.some((msg) => {
        // Check for exact timestamp match
        if (msg.timestamp === message.timestamp) return true;

        // Check for content + type match within a small time window
        const msgTime = new Date(message.timestamp || new Date()).getTime();
        const existingMsgTime = new Date(msg.timestamp).getTime();
        const timeDiff = Math.abs(msgTime - existingMsgTime);

        return (
          msg.content === message.content &&
          msg.type === message.type &&
          timeDiff < 5000 // Within 5 seconds
        );
      });

      if (!messageExists) {
        // FIXED: Clean up message structure for storage
        const messageForStorage = {
          type: message.type,
          content: message.content,
          language: language || selectedVoice?.lang || "en",
          timestamp: message.timestamp || new Date().toISOString(),
          isAudio: message.isAudio || false,
        };

        // Add additional properties for user audio messages
        if (message.type === "user" && message.isAudio) {
          messageForStorage.transcript = message.transcript;
          messageForStorage.audioDuration = message.audioDuration;
        }

        // Add additional properties for bot messages
        if (message.type === "bot" && message.isAudio) {
          messageForStorage.id = message.id;
        }

        talkChats.push(messageForStorage);
        localStorage.setItem("talk_chats", JSON.stringify(talkChats));
        console.log(
          "💾 Successfully saved talk message:",
          messageForStorage.type,
          messageForStorage.content.substring(0, 50)
        );
        console.log("📊 Total talk_chats messages:", talkChats.length);
      } else {
        console.log("⏭️ Skipped duplicate message");
      }
    },
    [selectedVoice, config.greetingMessage]
  );

  // REPLACE the saving useEffect with this improved version:
  useEffect(() => {
    if (activeSection === "talk" && messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      console.log(
        "🔄 Checking if should save message:",
        lastMsg?.type,
        lastMsg?.content?.substring(0, 50)
      );

      // Save ALL user and bot messages except greetings and recording messages
      if (lastMsg && lastMsg.type && lastMsg.content) {
        const isGreeting =
          lastMsg.content ===
            (config.greetingMessage || "How can I help you?") &&
          lastMsg.type === "bot";

        const isRecordingMessage =
          lastMsg.content === "Recording in progress..." ||
          lastMsg.content === "Stopped voice recording." ||
          lastMsg.content === "No speech detected. Recording stopped.";

        // Save if it's NOT a greeting and NOT a recording message
        if (!isGreeting && !isRecordingMessage) {
          console.log("✅ Message qualifies for saving, proceeding...");
          saveTalkChatMessage(lastMsg, selectedVoice?.lang || "en");
        } else {
          console.log("❌ Message does not qualify for saving:", {
            isGreeting,
            isRecordingMessage,
            content: lastMsg.content.substring(0, 50),
          });
        }
      } else {
        console.log("❌ Invalid message structure:", lastMsg);
      }
    }
  }, [
    messages,
    activeSection,
    saveTalkChatMessage,
    config.greetingMessage,
    selectedVoice,
  ]);

  return (
    <>
      <div
        className={`fixed ${
          isExpandedWindow ? "inset-0 " : "bottom-4 right-4"
        } z-50 w-full sm:w-auto max-w-[calc(100vw-1rem)]`}
      >
        {!isChatOpen && !isExpandedWindow && (
          <motion.button
            initial={{ opacity: 0, y: 50, scale: 0.5 }}
            animate={{
              opacity: isChatOpen ? 0 : 1,
              y: isChatOpen ? 50 : 0,
              scale: isChatOpen ? 0.5 : 1,
            }}
            transition={{
              delay: isChatOpen ? 0 : 0.5,
              type: "spring",
              stiffness: 200,
              damping: 15,
              mass: 1,
              duration: 0.6,
            }}
            className={`ml-auto block p-4 rounded-full shadow-lg transform-gpu
            ${!chatBotData ? "cursor-not-allowed opacity-50" : ""}`}
            style={{
              backgroundColor: config.color,
              color: customBranding.toggleStartColor,
            }}
            onClick={chatBotData ? toggleChat : undefined}
          >
            <MdMessage size={24} />
          </motion.button>
        )}

        <AnimatePresence>
          {(isChatOpen || isExpandedWindow) && (
            <motion.div
              layout
              initial={{ opacity: 0, y: 20, scale: 0.8 }}
              animate={{
                opacity: 1,
                y: 0,
                scale: 1,
              }}
              exit={{ opacity: 0, y: 20, scale: 0.8 }}
              transition={{
                layout: { 
                  type: "spring", 
                  stiffness: 300, 
                  damping: 25 
                },
                opacity: { duration: 0.3 },
                scale: { duration: 0.4 },
              }}
              className={`${
                isExpandedWindow
                  ? "w-screen h-screen"
                  : (isInChat && (activeSection === "talk" || activeSection === "chat" || activeSection === "chat-history"))
                    ? "w-[24rem] h-[35rem] min-h-[33rem] max-h-[90vh]"
                    : "w-[24rem] h-[32rem] min-h-[28rem] max-h-[90vh]"
              } flex flex-col rounded-lg shadow-xl relative overflow-hidden`}
            >
              {/* Chat header */}
              <VoiceBotHeader
                isInChat={isInChat}
                handleBackClick={handleBackClick}
                isLoggedIn={isLoggedIn}
                handleLogout={handleLogout}
                isExpandedWindow={isExpandedWindow}
                handleExpand={handleExpand}
                toggleChat={toggleChat}
                isPending={isPending}
                supportedLanguages={supportedLanguages}
                config={config}
                customBranding={customBranding}
                showChatHistory={showChatHistory}
                isAiChat={currentChat?.id && !isInExistingChat}
                selectedVoice={selectedHindiVoice}
                setSelectedVoice={setSelectedHindiVoice}
                activeSection={activeSection}
                hideBackButton={
                  activeSection === 'Initial' ||
                  (chatBotData?.module && (chatBotData.module.toLowerCase() === 'chat' || chatBotData.module.toLowerCase() === 'talk'))
                }
              />
              <div className="flex-1 flex flex-col overflow-hidden">
                {!isInChat ? (
                  <InitialScreen
                    customBranding={customBranding}
                    config={config}
                    handleTalkClick={handleTalkClick}
                    handleChatClick={handleChatClick}
                    isExpandedWindow={isExpandedWindow}
                  />
                ) : (
                  <>
                    {showChatHistory ? (
                      <ChatHistory
                        onStartNewChat={handleStartNewChat}
                        onSelectChat={handleSelectAiChat}
                        onSelectReminderChat={handleSelectReminderChat}
                        onSelectExistingChat={handleSelectExistingChat}
                        showParticipants={showParticipants}
                        setShowParticipants={setShowParticipants}
                      />
                    ) : isInExistingChat ? (
                      <NewChat
                        conversationId={currentChatId}
                        onBack={handleBackToChatHistory}
                        isInChatbot={true}
                      />
                    ) : (
                      <ChatContainer
                        chatBoxRef={chatBoxRef}
                        customBranding={customBranding}
                        messages={messages}
                        isTyping={isTyping}
                        config={config}
                        toggleAudioPlayback={toggleAudioPlayback}
                        audioPlaying={audioPlaying}
                        handleToggleSpeech={handleToggleSpeech}
                        playingMessageId={playingMessageId}
                        activeSection={activeSection}
                        setMessages={setMessages}
                        recordingState={recordingState}
                        setRecordingState={setRecordingState}
                        recordingDuration={recordingDuration}
                        setRecordingDuration={setRecordingDuration}
                        speechDetected={speechDetected}
                        setSpeechDetected={setSpeechDetected}
                        setIsTyping={setIsTyping}
                        setIsLoading={setIsLoading}
                        setAbortController={setAbortController}
                        showContactForm={showContactForm}
                        setShowContactForm={setShowContactForm}
                        showStatusForm={showStatusForm}
                        setShowStatusForm={setShowStatusForm}
                        showFeedbackForm={showFeedbackForm}
                        setShowFeedbackForm={setShowFeedbackForm}
                        showLoginForm={showLoginForm}
                        setShowLoginForm={setShowLoginForm}
                        currentChatId={currentChat?.id}
                        isLoggedIn={isLoggedIn}
                        setIsLoggedIn={setIsLoggedIn}
                        isWaitingForResponse={isWaitingForResponse}
                        setIsWaitingForResponse={setIsWaitingForResponse}
                      />
                    )}
                  </>
                )}
                <VoiceBotFooter config={config} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default VoiceBot;
